import { PURCHASER_API_BASE_URL, getHeaders } from "../Constants";

export interface FileUpload {
    id: number;
    userId: number;
    teamId: number;
    createdAt: string;
    fileSizeBytes: number;
    internalFilename: string;
    originalFilename: string;
    mimeType: string;
    containerName: string;
    storageProvider?: string | null;
}

export default {
    getUploadToken: (filename: string, mimeType: string, fileSizeBytes: number): Promise<{ url: string; id: number }> =>
        fetch(`${PURCHASER_API_BASE_URL}/uploads/token`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify({
                filename,
                mimeType,
                fileSizeBytes,
            }),
        }).then(res => res.json()),

    getUploadedFiles: (params?: GetUploadedFilesParams): Promise<UploadFileListResponse> => {
        const { fileUploadIds, includeAssociations } = params || {};
        let fetchUrl = `${PURCHASER_API_BASE_URL}/uploads?includeAssociations=${includeAssociations || false}`;
        if (fileUploadIds && fileUploadIds.length > 0) {
            fetchUrl += `&fileUploadIds=${fileUploadIds.join(",")}`;
        }
        if (params?.search) {
            fetchUrl += `&search=${encodeURIComponent(params.search)}`;
        }
        if (params?.page) {
            fetchUrl += `&page=${params.page}`;
        }
        if (params?.limit) {
            fetchUrl += `&limit=${params.limit}`;
        }
        return fetch(fetchUrl, { headers: getHeaders() }).then(res => res.json());
    },

    getUploadedFile: (id: string, base64InternalFilename: string): Promise<{ document: UploadedFile; url: { url: string } }> =>
        fetch(`${PURCHASER_API_BASE_URL}/uploads/${id}/${base64InternalFilename}`, { headers: getHeaders() }).then(res => res.json()),

    getUploadedFilePreview: async (id: number): Promise<FilePreview> => {
        const url = `${PURCHASER_API_BASE_URL}/uploads/${id}/preview`;
        return await fetch(url, { headers: getHeaders() }).then(res => res.json());
    },

    getProcessFile: (id: string): Promise<string> =>
        fetch(`${PURCHASER_API_BASE_URL}/uploads/process-document/${id}?async=true`, { headers: getHeaders() }).then(res => res.text()),

    askDocumentQuestion: (fileId: string, question: string): Promise<any> =>
        fetch(`${PURCHASER_API_BASE_URL}/uploads/${fileId}/ask-question`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify({ question }),
        }).then(res => res.json()),

    uploadEmailSignatureImage: async (file: File): Promise<{ url: string }> => {
        const formData = new FormData();
        formData.append("file", file);

        return await fetch(`${PURCHASER_API_BASE_URL}/uploads/email-signature-image`, {
            headers: {
                Authorization: getHeaders().Authorization,
            },
            method: "POST",
            body: formData,
        }).then(res => res.json());
    },
    uploadSecureFile: async (
        file: File,
        options: {
            geoRestrictions: string[];
            excludeFromMetadata: boolean;
            maxDownloadsPerRecipient?: number;
            expirationDate?: number | null;
        },
    ): Promise<FileUpload> => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("filename", file.name);
        formData.append("expirationDate", String(options.expirationDate));
        options.geoRestrictions.forEach(geoRestriction => formData.append("geoRestrictions", geoRestriction));
        formData.append("excludeFromMetadata", String(options.excludeFromMetadata));
        if (options.maxDownloadsPerRecipient !== undefined) {
            formData.append("maxDownloadsPerRecipient", String(options.maxDownloadsPerRecipient));
        }

        return await fetch(`${PURCHASER_API_BASE_URL}/uploads/secure`, {
            headers: {
                Authorization: getHeaders().Authorization,
            },
            method: "POST",
            body: formData,
        }).then(res => res.json());
    },
    getGeometryFile: (id: string, base64InternalFilename: string): Promise<{ document: UploadedFile; url: { url: string } }> =>
        fetch(`${PURCHASER_API_BASE_URL}/uploads/${id}/${base64InternalFilename}/geometry`, { headers: getHeaders() }).then(res => res.json()),
};

export type FilePreview = {
    id: number;
    originalFilename: string;
    mimeType: string;

    file_upload_doc_metadata: {
        cleanMarkdownContents: string | null;
        rawMarkdownContents: string | null;
    } | null;
    file_upload_spreadsheet_metadata: {
        cleanMarkdownContents: string | null;
        rawMarkdownContents: string | null;
    } | null;
    downloadUrl: string;
    previewUrl: string | null;
    previewMimeType: string | null;
};

export type UploadedFile = {
    id: string;
    internalFilename: string;
    originalFilename: string;
    cleanFilename: string;
    fileSizeBytes: number;
    mimeType: string;
    createdAt: string;
    file_upload_doc_metadata: {
        rawMarkdownContents: string;
        cleanMarkdownContents: string;
        date: string;
        genericExtraction: string;
        salesRepEmail: string;
        salesRepName: string;
        salesRepPhone: string;
        summary: string;
        title: string;
        poNumber: string;
        type: string;
        vendorAddress: string;
        vendorGeneralEmail: string;
        vendorName: string;
        vendorPhone: string;
        vendorWebsite: string;
    };
    user: {
        email: string;
        first_name?: string;
        last_name?: string;
    };
    createdByUser: {
        email: string;
        first_name?: string;
        last_name?: string;
    };
    file_upload_spreadsheet_metadata: {
        title: string;
        summary: string;
        type: string;
        itemCategories: string[];
        rawMarkdownContents: string;
        cleanMarkdownContents: string;
        worksheets: {
            rows: number;
            cols: number;
            name: string;
            cells: string;
        }[];
    };
    fileComparisonQuote: {
        id: number;
        fileComparison: {
            publicId: number;
            title: string;
        };
    }[];
    associatedEntities?: {
        publicId: string;
        type: string;
        createdAt: string; // ISO date string
        title: string;
        createdByUser?: {
            id: number;
            email: string;
            first_name: string;
            last_name: string;
        };
        mailboxId?: number;
        communicationId?: number;
        isOriginalSource?: boolean;
    }[];
};

export type UploadFileListResponse = {
    data: UploadedFile[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
};

type GetUploadedFilesParams = {
    fileUploadIds?: number[];
    includeAssociations?: boolean;
    page?: number;
    limit?: number;
    search?: string;
};
